import { useRyftManagementBusinessUpdate } from "~/hooks/api/v2/ryftManagement/useRyftManagementBusinessUpdate";
import { useRyftManagementDirectorUpdate } from "~/hooks/api/v2/ryftManagement/useRyftManagementDirectorUpdate";
import { useRyftManagementPayoutMethodUpdate } from "~/hooks/api/v2/ryftManagement/useRyftManagementPayoutMethodUpdate";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { Alert } from "~/components/alert/Alert";
import { useRyftNonHostedDashboardAccount } from "./components/RyftNonHostedDashboardAccountProvider.shared";
import type { RyftAccountGetResponseType } from "@sellar-io/api-v2/types";
import {
  Button,
  Card,
  CardContent,
  Icon,
  IconType,
} from "@sellar-io/components";
import {
  CountryCodeDictionary,
  nationalityCodeDictionary,
} from "@sellar-io/common";
import { useSearchParams } from "react-router";
import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "~/components/modal/Dialog";
import {
  RyftEditBusinessForm,
  type RyftBusinessFormValues,
  type RyftBusinessSubmissionValues,
} from "../components/RyftEditBusinessForm";
import {
  ryftBusinessTypeLabels,
  verificationFieldLabel,
} from "./RyftNonHostedDashboard.labels";
import {
  RyftEditDirectorForm,
  type RyftDirectorFormValues,
  type RyftDirectorSubmissionValues,
} from "../components/RyftEditDirectorForm";
import {
  RyftEditBankAccountForm,
  type RyftBankAccountFormValues,
  type RyftBankAccountSubmissionValues,
} from "../components/RyftEditBankAccountForm";
import { RYFT_TERMS_URL } from "../onboarding/constants";
import { getRyftBusinessErrors } from "./RyftNonHostedDashboard.shared";
import dayjs from "dayjs";

type Verification = RyftAccountGetResponseType["verification"];
type Address = NonNullable<
  RyftAccountGetResponseType["business"]["registeredAddress"]
>;

export const RyftNonHostedAccounts = () => {
  const { account } = useRyftNonHostedDashboardAccount();

  const { errors } = getRyftBusinessErrors(account);
  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <Card className="border border-zinc-200 bg-white">
        <CardContent className="divide-y divide-zinc-200 p-0">
          <BusinessSection
            account={account}
            needsAction={errors.some(
              (error) => error.type === "business-details",
            )}
          />
          <DirectorSection
            account={account}
            needsAction={errors.some(
              (error) => error.type === "director-details",
            )}
          />
          <BankAccountSection
            account={account}
            needsAction={errors.some((error) => error.type === "bank-account")}
          />

          <TermsSection account={account} />
        </CardContent>
      </Card>
    </div>
  );
};

const AccountSection = ({
  sectionId,
  title,
  summary,
  icon,
  needsAction = false,
  canEdit = false,
  details,
  renderForm,
}: {
  sectionId: string;
  title: string;
  summary: string;
  icon: IconType;
  needsAction?: boolean;
  canEdit: boolean;
  details: ReactNode;
  renderForm?: (dismiss: () => void) => ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("section") === sectionId;
  const [editing, setEditing] = useState(false);

  const open = () => {
    setEditing(false);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("section", sectionId);
      return next;
    });
  };

  const dismiss = () => {
    setEditing(false);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("section");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <>
      <div className="flex items-center gap-3 p-4 md:gap-4 md:p-5">
        <Icon icon={icon} size={1.2} className="shrink-0 text-zinc-500" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-zinc-900">{title}</h2>
            {needsAction && (
              <span className="text-xs font-medium text-amber-800">
                Action required
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-sm text-zinc-600">{summary}</p>
        </div>
        <Button
          color="slim"
          aria-label={`View ${title.toLowerCase()}`}
          onClick={open}
        >
          View <Icon icon="CHEVRON_RIGHT" size={0.8} />
        </Button>
      </div>
      <Dialog active={active} onDismiss={dismiss}>
        <DialogNavigation
          title={editing ? `Edit ${title.toLowerCase()}` : title}
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={dismiss}
        />
        <DialogContent className="flex flex-col gap-5">
          {active && (
            <>
              {editing && renderForm ? renderForm(dismiss) : details}
              {!editing && canEdit && renderForm && (
                <div className="flex justify-end">
                  <Button color="secondary" onClick={() => setEditing(true)}>
                    Edit {title.toLowerCase()}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Details = ({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>;
}) => (
  <dl className="divide-y divide-zinc-100">
    {items.map(({ label, value }) => (
      <div
        key={label}
        className="grid gap-1 py-3 sm:grid-cols-[180px_1fr] sm:gap-4"
      >
        <dt className="text-sm text-zinc-500">{label}</dt>
        <dd className="min-w-0 text-sm break-words text-zinc-900">
          {value || "Not provided"}
        </dd>
      </div>
    ))}
  </dl>
);

const VerificationFeedback = ({
  verification,
}: {
  verification: Verification;
}) => {
  const errors =
    verification.errors?.filter((error) => error.code === "InvalidField") ?? [];
  const fields = verification.requiredFields ?? [];
  if (!errors.length && !fields.length) return null;
  return (
    <div className="rounded-lg bg-amber-50 p-3 text-sm">
      <p className="font-semibold text-amber-900">Information required</p>
      {fields.map(({ name }) => (
        <p key={name} className="mt-1 text-amber-900">
          Required: {verificationFieldLabel(name)}
        </p>
      ))}
      {errors.map((error) => (
        <p key={`${error.code}-${error.id}`} className="mt-1 text-red-700">
          {verificationFieldLabel(error.id)}: {error.description}
        </p>
      ))}
    </div>
  );
};

const formatAddress = (address: Address | null | undefined) => {
  if (!address) return "Not provided";
  return [
    address.lineOne,
    address.city,
    address.postalCode,
    CountryCodeDictionary[address.country],
  ]
    .filter(Boolean)
    .join(", ");
};

const BusinessSection = ({
  account,
  needsAction,
}: {
  account: RyftAccountGetResponseType;
  needsAction: boolean;
}) => {
  const { business } = account;
  const initialData: RyftBusinessFormValues = {
    businessName: business.name,
    businessType: business.type,
    companyRegistrationNumber: business.registrationNumber ?? "",
    addressLine: business.registeredAddress?.lineOne ?? "",
    city: business.registeredAddress?.city ?? "",
    postalCode: business.registeredAddress?.postalCode ?? "",
    contactEmail: business.contactEmail ?? "",
  };
  return (
    <AccountSection
      sectionId="business"
      title="Business details"
      icon="STOREFRONT_OUTLINE"
      summary={business.name}
      canEdit={true}
      needsAction={needsAction}
      details={
        <>
          <VerificationFeedback verification={account.verification} />
          <Details
            items={[
              { label: "Legal business name", value: business.name },
              {
                label: "Business type",
                value: ryftBusinessTypeLabels[business.type] ?? business.type,
              },
              {
                label: "Registration number",
                value: business.registrationNumber,
              },
              {
                label: "Registered address",
                value: formatAddress(business.registeredAddress),
              },
              { label: "Contact email", value: business.contactEmail },
            ]}
          />
        </>
      }
      renderForm={(dismiss) => (
        <>
          <Alert
            type="warning"
            message="If you change these details, Ryft may need to review them again."
            className="w-full"
          />
          <VerificationFeedback verification={account.verification} />
          <BusinessEditForm initialData={initialData} onComplete={dismiss} />
        </>
      )}
    />
  );
};

const BusinessEditForm = ({
  initialData,
  onComplete,
}: {
  initialData: RyftBusinessFormValues;
  onComplete: () => void;
}) => {
  const updateBusinessMutation = useRyftManagementBusinessUpdate();

  const errorMessage =
    apiV2AssertError(updateBusinessMutation.error)?.response?.data.message ??
    "Unable to update your business details. Please try again.";

  const handleUpdateBusiness = async (values: RyftBusinessSubmissionValues) => {
    await updateBusinessMutation.mutateAsync(
      {
        name: values.businessName,
        type: values.businessType,
        registrationNumber: values.companyRegistrationNumber,
        registeredAddress: {
          lineOne: values.addressLine,
          city: values.city,
          country: "GB",
          postalCode: values.postalCode,
        },
        contactEmail: values.contactEmail,
      },
      { onSuccess: onComplete },
    );
  };

  return (
    <>
      {updateBusinessMutation.isError && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <RyftEditBusinessForm
        mode="update"
        initialData={initialData}
        isSubmitting={updateBusinessMutation.isPending}
        onSubmit={handleUpdateBusiness}
      />
    </>
  );
};

const DirectorSection = ({
  account,
  needsAction,
}: {
  account: RyftAccountGetResponseType;
  needsAction: boolean;
}) => {
  const director = account.directors[0];
  if (!director)
    return (
      <AccountSection
        sectionId="director"
        title="Director"
        icon="ACCOUNT_MULTIPLE_OUTLINE"
        summary="Director missing"
        details={<p className="text-sm text-zinc-600">Not provided</p>}
        canEdit={false}
      />
    );

  const name = [director.firstName, director.middleNames, director.lastName]
    .filter(Boolean)
    .join(" ");

  const initialData: RyftDirectorFormValues = {
    firstName: director.firstName,
    middleName: director.middleNames ?? "",
    lastName: director.lastName,
    email: director.email ?? "",
    dateOfBirth: director.dateOfBirth ?? "",
    countryOfBirth: director.countryOfBirth ?? "",
    gender: director.gender ?? "",
    nationalities: director.nationalities ?? [],
    phoneNumber: director.phoneNumber ?? "",
    address: {
      lineOne: director.address?.lineOne ?? "",
      city: director.address?.city ?? "",
      country: director.address?.country ?? "",
      postalCode: director.address?.postalCode ?? "",
    },
  };

  return (
    <AccountSection
      sectionId="director"
      title="Director"
      icon="ACCOUNT_MULTIPLE_OUTLINE"
      summary={name}
      canEdit={true}
      needsAction={needsAction}
      details={
        <>
          <VerificationFeedback verification={director.verification} />
          <Details
            items={[
              { label: "Name", value: name },
              { label: "Email", value: director.email },
              { label: "Date of birth", value: director.dateOfBirth },
              {
                label: "Country of birth",
                value:
                  director.countryOfBirth &&
                  CountryCodeDictionary[director.countryOfBirth],
              },
              { label: "Gender", value: director.gender },
              {
                label: "Nationalities",
                value: director.nationalities
                  ?.map((code) => nationalityCodeDictionary[code])
                  .join(", "),
              },
              { label: "Address", value: formatAddress(director.address) },
              { label: "Phone number", value: director.phoneNumber },
            ]}
          />
        </>
      }
      renderForm={(dismiss) => (
        <>
          <Alert
            type="warning"
            message="If you change these details, Ryft may need to review them again."
            className="w-full"
          />
          <VerificationFeedback verification={director.verification} />
          <DirectorEditForm
            directorId={director.id}
            initialData={initialData}
            onComplete={dismiss}
          />
        </>
      )}
    />
  );
};

const DirectorEditForm = ({
  directorId,
  initialData,
  onComplete,
}: {
  directorId: string;
  initialData: RyftDirectorFormValues;
  onComplete: () => void;
}) => {
  const updateDirectorMutation = useRyftManagementDirectorUpdate();

  const errorMessage =
    apiV2AssertError(updateDirectorMutation.error)?.response?.data.message ??
    "Unable to update your director details. Please try again.";

  const handleUpdateDirector = async (values: RyftDirectorSubmissionValues) => {
    await updateDirectorMutation.mutateAsync(
      {
        directorId,
        data: {
          firstName: values.firstName,
          middleNames: values.middleName || null,
          lastName: values.lastName,
          email: values.email,
          dateOfBirth: values.dateOfBirth,
          countryOfBirth: values.countryOfBirth,
          gender: values.gender,
          nationalities: values.nationalities,
          address: values.address,
          phoneNumber: values.phoneNumber,
        },
      },
      { onSuccess: onComplete },
    );
  };

  return (
    <>
      {updateDirectorMutation.isError && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <RyftEditDirectorForm
        mode="update"
        initialData={initialData}
        isSubmitting={updateDirectorMutation.isPending}
        onSubmit={handleUpdateDirector}
      />
    </>
  );
};

const BankAccountSection = ({
  account,
  needsAction,
}: {
  account: RyftAccountGetResponseType;
  needsAction: boolean;
}) => {
  const method = account.payoutMethods[0];
  if (!method)
    return (
      <AccountSection
        sectionId="bank"
        title="Payout bank account"
        icon="BANK"
        summary="No bank account submitted"
        details={
          <p className="text-sm text-zinc-600">
            No bank account has been submitted.
          </p>
        }
        canEdit={true}
      />
    );
  const bank = method.bankAccount;
  const summary =
    [
      bank?.bankName || method.displayName,
      bank?.last4 && `Account ending ${bank.last4}`,
    ]
      .filter(Boolean)
      .join(" · ") || "Bank account submitted";
  const initialData: RyftBankAccountFormValues = {
    sortCode: bank?.bankId ?? "",
    accountNumber: "",
    accountHolderAddress: {
      lineOne: bank?.address?.lineOne ?? "",
      city: bank?.address?.city ?? "",
      country: bank?.address?.country ?? "",
      postalCode: bank?.address?.postalCode ?? "",
    },
  };
  return (
    <AccountSection
      sectionId="bank"
      title="Payout bank account"
      icon="BANK"
      summary={summary}
      canEdit={true}
      needsAction={needsAction}
      details={
        <>
          {method.invalidReason && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-red-700">
              {method.invalidReason}
            </p>
          )}
          <Details
            items={[
              { label: "Bank", value: bank?.bankName },
              { label: "Account name", value: method.displayName },
              { label: "Sort code", value: bank?.bankId },
              {
                label: "Account number",
                value: bank?.last4 && `•••• ${bank.last4}`,
              },
              { label: "Currency", value: method.currency },
              {
                label: "Account holder address",
                value: formatAddress(bank?.address),
              },
              { label: "Status", value: method.status },
            ]}
          />
        </>
      }
      renderForm={(dismiss) => (
        <>
          <p className="text-sm text-zinc-600">
            {summary}. Enter the full account number again to update these
            details.
          </p>
          <BankAccountEditForm
            payoutMethodId={method.id}
            initialData={initialData}
            onComplete={dismiss}
          />
        </>
      )}
    />
  );
};

const BankAccountEditForm = ({
  payoutMethodId,
  initialData,
  onComplete,
}: {
  payoutMethodId: string;
  initialData: RyftBankAccountFormValues;
  onComplete: () => void;
}) => {
  const updatePayoutMethodMutation = useRyftManagementPayoutMethodUpdate();

  const errorMessage =
    apiV2AssertError(updatePayoutMethodMutation.error)?.response?.data
      .message ?? "Unable to update your bank details. Please try again.";

  const handleUpdateBankAccount = async (
    values: RyftBankAccountSubmissionValues,
  ) => {
    await updatePayoutMethodMutation.mutateAsync(
      { payoutMethodId, data: values },
      { onSuccess: onComplete },
    );
  };

  return (
    <>
      {updatePayoutMethodMutation.isError && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <RyftEditBankAccountForm
        mode="update"
        initialData={initialData}
        isSubmitting={updatePayoutMethodMutation.isPending}
        onSubmit={handleUpdateBankAccount}
      />
    </>
  );
};

const TermsSection = ({ account }: { account: RyftAccountGetResponseType }) => {
  const acceptance = account.termsOfService?.acceptance;
  const acceptedAt = acceptance
    ? dayjs.unix(acceptance.when).format("D MMMM YYYY")
    : "Not accepted";
  return (
    <AccountSection
      sectionId="terms"
      title="Terms and conditions"
      icon="CLIPBOARD_CHECK_OUTLINE"
      summary={acceptance ? `Accepted ${acceptedAt}` : "Not accepted"}
      canEdit={false}
      details={
        <>
          <Details items={[{ label: "Accepted on", value: acceptedAt }]} />
          <a
            href={RYFT_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium underline underline-offset-2"
          >
            Read Ryft's terms and conditions
          </a>
        </>
      }
    />
  );
};
